import React from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { CreditApplicationBankSource } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { FormTextInput } from '@divvy-web/skylab.form'

import { useGetCreditApplicationForReview } from '@sputnik/pages/CreditApplicationPages/ReviewForm/GetCreditApplicationForReview.gql'
import SectionCollapsibleList from '@sputnik/pages/CreditApplicationPages/ReviewForm/SectionCollapsibleList'
import { hasEmptyValue, hasMissingRequiredField } from '@sputnik/pages/CreditApplicationPages/ReviewForm/utils'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import styles from './styles.module.scss'

const BankAccountReviewSection = () => {
  const navigate = useNavigate()
  const { appId } = useParams()
  const { control } = useForm()
  const [getClassName] = useNameFormatter('BankAccountReviewSection')
  const { tracker } = useWebTracking()

  const { data } = useGetCreditApplicationForReview({
    variables: {
      creditApplicationId: appId ?? '',
    },
  })

  const { selectedBankInfo } = data?.creditApplication?.financeInfo ?? {}
  const { accountHolderName, accountNumberLastFour, bankName, routingNumber, source } = selectedBankInfo ?? {}

  const isManualSource = source === CreditApplicationBankSource.MANUAL

  const handleOnEditClick = () => {
    tracker?.track('se_signup__review_and_sign_edit_info__fired', {
      application_section: 'Bank info',
      navigated_to_page: `${PATHNAME_CONSTS.CONNECT_BANK_ACCOUNT_PATH}`,
    })
    navigate(`../${PATHNAME_CONSTS.CONNECT_BANK_ACCOUNT_PATH}`)
  }

  const hasMissingInfo =
    !!data &&
    hasMissingRequiredField([
      ...(isManualSource ? [accountHolderName] : []),
      accountNumberLastFour,
      bankName,
      routingNumber,
    ])

  return (
    <div className={`${styles[getClassName('')]} fs-unmask`}>
      <SectionCollapsibleList
        hasMissingInfo={hasMissingInfo}
        headerText={
          <FormattedMessage
            defaultMessage='Bank info'
            id='sputnik.BankAccountReviewSection__iG2v2J'
          />
        }
        onEditClick={handleOnEditClick}
      >
        <FormTextInput
          readOnly
          className='fs-mask'
          control={control}
          isRequired={hasEmptyValue(bankName)}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Bank name'
                id='sputnik.BankAccountReviewSection__QOLh4y'
              />
            </span>
          }
          name='bankName'
          value={bankName || '—'}
        />
        <FormTextInput
          readOnly
          className='fs-mask'
          control={control}
          isRequired={hasEmptyValue(accountHolderName) && isManualSource}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Account holder name'
                id='sputnik.BankAccountReviewSection__kqidNP'
              />
            </span>
          }
          name='accountHolderName'
          value={accountHolderName || '—'}
        />
        <FormTextInput
          readOnly
          className='fs-mask'
          control={control}
          isRequired={hasEmptyValue(accountNumberLastFour)}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Account number'
                id='sputnik.BankAccountReviewSection__/BgyNa'
              />
            </span>
          }
          name='accountNumberLastFour'
          value={accountNumberLastFour ? `****${accountNumberLastFour}` : '—'}
        />
        <FormTextInput
          readOnly
          className='fs-mask'
          control={control}
          isRequired={hasEmptyValue(selectedBankInfo?.routingNumber)}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Routing number'
                id='sputnik.BankAccountReviewSection__Pk5sml'
              />
            </span>
          }
          name='routingNumber'
          value={selectedBankInfo?.routingNumber || '—'}
        />
      </SectionCollapsibleList>
    </div>
  )
}

export default BankAccountReviewSection
